"use client";

import { Ellipsis, Mic, MonitorUp, PhoneOff, Smile, Video } from "lucide-react";
import { AnimatePresence, motion } from "motion/react";
import { useTranslations } from "next-intl";
import { useEffect, useState } from "react";

import { cn } from "@/lib/utils";

import { useSequence } from "../use-sequence";
import { AppWindow, Avatar } from "../visual-kit";

// =============================================================================

const DURATIONS = [2600, 2600, 2800];
// Speaker of each caption: 0 = me, 1 = the client
const SPEAKERS = [0, 1, 0];
const REACTIONS = [null, "😍", "👍"];

const PARTICIPANTS = [
  { name: "Bryan Cellier", short: "Bryan", color: 4 },
  { name: "Jean Buisson", short: "Jean", color: 0 },
];

/**
 * Miniature of the validated website, shared on screen during the call.
 */
function SharedScreen({
  brand,
  headline,
  cta,
}: {
  brand: string;
  headline: string;
  cta: string;
}) {
  return (
    <div className="flex size-full flex-col overflow-hidden rounded-[0.6cqw] bg-[#fbf8f1]">
      <div className="flex items-center justify-between bg-[#14281D] px-[1.4cqw] py-[0.9cqw]">
        <span className="text-[1.1cqw] font-bold text-[#E9D8A6]">{brand}</span>
        <span className="flex gap-[0.9cqw]">
          {[0, 1, 2].map((item) => (
            <span
              key={item}
              className="h-[0.5cqw] w-[2.6cqw] rounded-full bg-white/40"
            />
          ))}
        </span>
      </div>
      <div className="grid flex-1 grid-cols-[1.1fr_1fr] items-center gap-[5%] px-[6%]">
        <div className="flex flex-col gap-[1.2cqw]">
          <p className="text-[2.1cqw] leading-[1.05] font-extrabold tracking-[-0.03em] text-[#14281D]">
            {headline}
          </p>
          <span className="w-fit rounded-full bg-[#C9A227] px-[1.4cqw] py-[0.7cqw] text-[0.95cqw] font-semibold text-[#14281D]">
            {cta}
          </span>
        </div>
        <div className="grid aspect-square place-items-center rounded-[1cqw] bg-[radial-gradient(circle_at_30%_25%,#ffd27a,#e07a3f_45%,#7a2e16)] text-[5cqw]">
          🍝
        </div>
      </div>
    </div>
  );
}

/**
 * Step 5: a video call where I present the mockups to the client.
 */
export function MeetingVisual({ active }: { active: boolean }) {
  const t = useTranslations("Home.process.visuals.meeting");
  const tMockup = useTranslations("Home.process.visuals.mockup");
  const captions = t.raw("captions") as string[];
  const step = useSequence(active, DURATIONS);
  const speaker = SPEAKERS[step];
  const reaction = REACTIONS[step];

  // Call timer
  const [seconds, setSeconds] = useState(768);
  useEffect(() => {
    if (!active) return;
    const interval = window.setInterval(
      () => setSeconds((value) => value + 1),
      1000
    );
    return () => window.clearInterval(interval);
  }, [active]);
  const timer = `${String(Math.floor(seconds / 60)).padStart(2, "0")}:${String(seconds % 60).padStart(2, "0")}`;

  return (
    <AppWindow dark className="bg-[#1f1f22]">
      <div className="flex size-full flex-col p-[1.4cqw] text-white">
        {/* Header */}
        <div className="flex items-center justify-between text-[1cqw]">
          <span className="flex items-center gap-[0.6cqw] rounded-full bg-white/10 px-[0.9cqw] py-[0.35cqw]">
            <motion.span
              className="size-[0.8cqw] rounded-full bg-[#ef4444]"
              animate={{ opacity: [1, 0.25, 1] }}
              transition={{ duration: 1.4, repeat: Infinity }}
            />
            {t("recording")}
          </span>
          <span className="font-mono text-white/70 tabular-nums">{timer}</span>
        </div>

        <div className="mt-[1.2cqw] flex min-h-0 flex-1 gap-[1.2cqw]">
          {/* Stage */}
          <div className="relative min-w-0 flex-1 overflow-hidden rounded-[1cqw] bg-[#2a2a2e] p-[1.2cqw]">
            <p className="mb-[0.8cqw] text-[0.95cqw] text-white/60">
              {t("presenting")}
            </p>
            <div className="h-[calc(100%-2.2cqw)]">
              <SharedScreen
                brand={tMockup("brand")}
                headline={tMockup("headline")}
                cta={tMockup("cta")}
              />
            </div>
            {/* Captions */}
            <div className="absolute inset-x-[8%] bottom-[2.4cqw] flex justify-center">
              <AnimatePresence mode="wait">
                <motion.p
                  key={step}
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -6 }}
                  transition={{ duration: 0.3 }}
                  className="rounded-[0.6cqw] bg-black/75 px-[1.2cqw] py-[0.6cqw] text-center text-[1.05cqw] leading-snug"
                >
                  <span className="font-semibold text-white/60">
                    {PARTICIPANTS[speaker].short} :{" "}
                  </span>
                  {captions[step]}
                </motion.p>
              </AnimatePresence>
            </div>
          </div>

          {/* Participants */}
          <div className="flex w-[24%] shrink-0 flex-col gap-[1.2cqw]">
            {PARTICIPANTS.map((participant, index) => {
              const isSpeaking = speaker === index;
              return (
                <div
                  key={participant.name}
                  className="relative grid flex-1 place-items-center overflow-hidden rounded-[1cqw] bg-[#2a2a2e]"
                >
                  <span className="relative grid place-items-center">
                    {isSpeaking && (
                      <motion.span
                        className="absolute inset-0 rounded-full bg-[#22c55e]"
                        animate={{
                          scale: [1, 1.35, 1],
                          opacity: [0.5, 0, 0.5],
                        }}
                        transition={{ duration: 1.2, repeat: Infinity }}
                      />
                    )}
                    <Avatar
                      name={participant.name}
                      colorIndex={participant.color}
                      className={cn(
                        "relative size-[5.4cqw] text-[1.8cqw] ring-[0.25cqw] transition-[box-shadow]",
                        isSpeaking ? "ring-[#22c55e]" : "ring-transparent"
                      )}
                    />
                  </span>
                  <span className="absolute bottom-[0.8cqw] left-[0.9cqw] text-[0.9cqw] text-white/80">
                    {participant.short}
                  </span>
                </div>
              );
            })}
          </div>
        </div>

        {/* Controls */}
        <div className="mt-[1.2cqw] flex items-center justify-center gap-[1cqw]">
          {[Mic, Video, MonitorUp, Smile, Ellipsis].map((Icon, index) => (
            <span
              key={index}
              className={cn(
                "grid size-[3.2cqw] place-items-center rounded-full",
                index === 2 ? "bg-[#8ab4f8] text-[#1f1f22]" : "bg-white/10"
              )}
            >
              <Icon className="size-[1.4cqw]" />
            </span>
          ))}
          <span className="grid h-[3.2cqw] w-[5cqw] place-items-center rounded-full bg-[#ea4335]">
            <PhoneOff className="size-[1.4cqw]" />
          </span>
        </div>

        {/* Floating reaction */}
        <AnimatePresence>
          {reaction && active && (
            <motion.span
              key={`${step}-${reaction}`}
              className="absolute right-[30%] bottom-[12cqw] text-[3.4cqw]"
              initial={{ opacity: 0, y: 0, scale: 0.6 }}
              animate={{
                opacity: [0, 1, 1, 0],
                y: -110,
                scale: [0.6, 1.15, 1, 1],
              }}
              exit={{ opacity: 0 }}
              transition={{ duration: 2.2, ease: "easeOut" }}
            >
              {reaction}
            </motion.span>
          )}
        </AnimatePresence>
      </div>
    </AppWindow>
  );
}

// =============================================================================
